import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Icon, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { brl, cycleLabel } from '../lib/format';
import { usePageMeta } from '../lib/seo';
import { tr } from '../lib/i18n';
import { CurrencyNote } from '../components/LanguageSelect';

type Cycle = 'monthly' | 'semiannual' | 'yearly';

interface PlanFeature {
  key: string;
  enabled: boolean;
}

interface Plan {
  key: string;
  name: string;
  description: string;
  priceMonthCents: number;
  priceSemiannualCents: number;
  priceYearCents: number;
  annualDiscountPercent: number;
  maxProfessionals: number;
  features: PlanFeature[];
}

interface PlansResponse {
  trialDays: number;
  paymentConfigured: boolean;
  pixEnabled: boolean;
  plans: Plan[];
}

const FEATURE_LABELS: Record<string, string> = {
  online_booking: 'Agendamento online',
  public_profile: 'Perfil público',
  agenda_daily: 'Agenda diária',
  agenda_weekly: 'Agenda semanal',
  agenda_monthly: 'Agenda mensal',
  customer_management: 'Gestão de clientes',
  history: 'Histórico de atendimentos',
  public_link: 'Link público compartilhável',
  whatsapp_shortcut: 'Atalho de WhatsApp',
  schedule_config: 'Configuração de horários',
  team: 'Gestão de equipe',
  individual_agendas: 'Agendas individuais por profissional',
  reports: 'Relatórios',
  blocked_periods: 'Períodos bloqueados',
  cancellation_history: 'Histórico de cancelamentos',
  report_export: 'Exportação de relatórios',
  extended_gallery: 'Portfólio ampliado',
  permissions: 'Permissões por função',
  central_agenda: 'Agenda central do estabelecimento',
  advanced_reports: 'Relatórios avançados',
  kpi: 'Indicadores de desempenho',
  services_per_professional: 'Serviços por profissional',
  business_settings: 'Configurações do estabelecimento'
};

function featureLabel(key: string): string {
  const label = FEATURE_LABELS[key];
  return label ? tr(label) : key.replace(/_/g, ' ');
}

export function PricingPage(): JSX.Element {
  usePageMeta(
    tr('Planos e preços'),
    tr('Veja os planos da AgendaPro, o teste gratuito de 7 dias e tudo que cada assinatura inclui.'),
    '/precos'
  );

  const [data, setData] = useState<PlansResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cycle, setCycle] = useState<Cycle>('yearly');

  useEffect(() => {
    let alive = true;
    api
      .get<PlansResponse>('/api/plans')
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err: unknown) => {
        if (alive) setError(err instanceof ApiError ? err.message : tr('Não foi possível carregar os planos.'));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="mx-auto w-full max-w-content px-4 py-12">
      <header className="text-center">
        <h1 className="font-display text-4xl font-semibold text-ink">{tr("Planos e preços")}</h1>
        <p className="mx-auto mt-3 max-w-xl text-muted">
          {tr("Escolha o plano pelo tamanho da sua equipe. Todos começam com")} {data ? data.trialDays : 7} {tr("dias de teste gratuito; depois disso é necessária uma assinatura paga.")}
        </p>
      </header>

      <div className="mt-6 flex justify-center">
        <div
          className="inline-flex rounded-full border border-line bg-card p-1"
          role="group"
          aria-label={tr("Ciclo de cobrança")}
        >
          {(['monthly', 'semiannual', 'yearly'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setCycle(option)}
              aria-pressed={cycle === option}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-all ${cycle === option ? 'bg-accent text-on-accent shadow-[0_8px_16px_-10px_var(--nl-accent)]' : 'text-muted hover:text-graphite'}`}
            >
              {cycleLabel(option)}
              {option !== 'monthly' ? <span className="ml-1.5 text-xs opacity-80">{tr("economia")}</span> : null}
            </button>
          ))}
        </div>
      </div>

      <CurrencyNote className="mt-3 text-center" />

      <div aria-live="polite" className="mt-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner label={tr("Carregando planos")} />
          </div>
        ) : error ? (
          <Alert tone="danger" title={tr("Erro ao carregar")}>
            {error}
          </Alert>
        ) : data ? (
          <>
            {data.paymentConfigured ? null : data.pixEnabled ? (
              <div className="mb-6">
                <Alert tone="info" title={tr("Pagamento por Pix")}>
                  {tr("A contratação é feita por Pix: no painel, em")} <strong>{tr("Meu plano")}</strong>{tr(", você escolhe o plano e recebe um QR Code com o valor exato. A licença é ativada quando a equipe confirma o comprovante — nenhuma cobrança automática é feita no cartão.")}
                </Alert>
              </div>
            ) : (
              <div className="mb-6">
                <Alert tone="warning" title={tr("Cobrança online ainda não configurada")}>
                  {tr("A integração de pagamentos da AgendaPro ainda não está ativa neste ambiente. Você pode conhecer os planos e começar o teste gratuito, mas a contratação paga e o checkout online serão liberados pela equipe assim que o provedor estiver configurado. Nenhuma cobrança será feita agora.")}
                </Alert>
              </div>
            )}
            <div className="grid gap-6 lg:grid-cols-3">
              {data.plans.map((plan) => (
                <PlanCard key={plan.key} plan={plan} cycle={cycle} paymentConfigured={data.paymentConfigured} pixEnabled={data.pixEnabled} />
              ))}
            </div>
            <p className="mt-8 text-center text-sm text-muted">
              {tr("Preços em reais (BRL). Os valores semestral e anual já refletem o desconto do ciclo e são cobrados em uma única vez.")}
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}

function PlanCard({ plan, cycle, paymentConfigured, pixEnabled }: { plan: Plan; cycle: Cycle; paymentConfigured: boolean; pixEnabled: boolean }): JSX.Element {
  const included = plan.features.filter((f) => f.enabled);
  const excluded = plan.features.filter((f) => !f.enabled);
  const price = cycle === 'yearly' ? plan.priceYearCents : cycle === 'semiannual' ? plan.priceSemiannualCents : plan.priceMonthCents;
  const months = cycle === 'yearly' ? 12 : cycle === 'semiannual' ? 6 : 1;
  const perMonth = Math.round(price / months);

  return (
    <div className="flex flex-col rounded-lg border border-line bg-card p-6 shadow-sm">
      <h2 className="font-display text-2xl font-semibold text-ink">{plan.name}</h2>
      <p className="mt-1.5 min-h-[2.5rem] text-sm text-muted">{plan.description ? tr(plan.description) : plan.description}</p>

      <div className="mt-4">
        <div className="flex items-end gap-1.5">
          <span className="font-display text-3xl font-semibold text-graphite">{brl(price)}</span>
          <span className="pb-1 text-sm text-muted">/{cycle === 'yearly' ? tr('ano') : cycle === 'semiannual' ? tr('semestre') : tr('mês')}</span>
        </div>
        {cycle === 'monthly' ? (
          <p className="mt-1 text-sm text-muted">{tr("ou")} {brl(plan.priceSemiannualCents)}{tr("/semestre ·")} {brl(plan.priceYearCents)}{tr("/ano com")} {plan.annualDiscountPercent}{tr("% off")}</p>
        ) : (
          <p className="mt-1 text-sm text-sage">
            {tr("equivale a")} {brl(perMonth)}{tr("/mês")}{cycle === 'yearly' ? ` · ${plan.annualDiscountPercent}${tr('% de desconto')}` : ''}
          </p>
        )}
      </div>

      <p className="mt-4 inline-flex items-center gap-2 rounded-md bg-surface px-3 py-2 text-sm text-ink">
        <Icon name="user" />
        {tr("Até")} {plan.maxProfessionals} {plan.maxProfessionals === 1 ? tr('profissional') : tr('profissionais')}
      </p>

      <Link to="/comecar" className="mt-5">
        <Button className="w-full">{tr("Começar teste grátis")}</Button>
      </Link>
      {!paymentConfigured && !pixEnabled ? (
        <p className="mt-2 text-center text-xs text-muted">{tr("Checkout online indisponível por enquanto.")}</p>
      ) : pixEnabled && !paymentConfigured ? (
        <p className="mt-2 text-center text-xs text-muted">{tr("Pagamento por Pix, com ativação após a confirmação do comprovante.")}</p>
      ) : null}

      <ul className="mt-5 space-y-2">
        {included.map((f) => (
          <li key={f.key} className="flex gap-2 text-sm text-ink">
            <span className="mt-0.5 shrink-0 text-sage"><Icon name="check" size={16} /></span>
            {featureLabel(f.key)}
          </li>
        ))}
        {excluded.map((f) => (
          <li key={f.key} className="flex gap-2 text-sm text-muted">
            <span className="mt-0.5 shrink-0"><Icon name="close" size={16} /></span>
            <span className="line-through">{featureLabel(f.key)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
